/**
 * Answer where you are (ADR 0072): one sheet, mounted once in the shell, that any surface opens
 * with `openAnswer(id)` instead of sending the person to /inbox. It holds the shared request card
 * (RequestCard: the four actions, the patterns, the high-risk confirm), the question card for a
 * question, plan or form (AskCard), or the blocked card (Approve and resume, Mark as handled).
 * "Open in Inbox" stays as a secondary link.
 */
import { CircleCheckBig, Hand, Inbox, SquareTerminal } from '@orgus/ui/icons';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { create } from 'zustand';
import { Button, Dialog, EmptyState, useNow } from '@orgus/ui';
import type { ApprovalRequest } from '@orgus/protocol';
import { useOrgus } from '../../lib/store.ts';
import { usePersonName } from '../missions/people.ts';
import { ApprovalBlockCard } from './ApprovalBlocker.tsx';
import { AskAnswer, TimeLeft, askHeading } from './AskAnswer.tsx';
import type { AskParts } from './AskAnswer.tsx';
import { isAnswerable } from './askModel.ts';
import { useSessionTitle } from '../sessions/title.tsx';
import { blockView, grantUntil, useBlockedApprovals, useMeId } from './blocked.ts';
import { useApprovals } from './live.ts';
import { RequestCard } from './RequestCard.tsx';
import { useSessionParam } from '../sessions/useSessionParam.ts';

interface AnswerSheetState {
  id: string | null;
  open: (id: string) => void;
  close: () => void;
}

export const useAnswerSheet = create<AnswerSheetState>((set) => ({
  id: null,
  open: (id) => set({ id }),
  close: () => set({ id: null }),
}));

/** Open the answer sheet for one request (pending or blocked), wherever the person is. */
export const openAnswer = (id: string): void => useAnswerSheet.getState().open(id);

/** The approval a console link points at (`/inbox?approval=<id>`), or null. */
export function approvalOfUrl(url: string): string | null {
  try {
    const u = new URL(url, 'http://orgus.local');
    return u.pathname === '/inbox' || u.pathname === '/m' ? u.searchParams.get('approval') : null;
  } catch {
    return null;
  }
}

/**
 * A question, plan or form as the sheet itself (no card inside the dialog): the heading with its
 * hand and the time left up top beside the close button, the questions scrolling, and the send bar
 * pinned under them, so Send answer is always in reach, on a phone too.
 */
function AskSheet({ a, onClose }: { a: ApprovalRequest; onClose: () => void }) {
  const now = useNow(15_000);
  const session = useOrgus((st) => st.sessions[a.sessionId]);
  const titleOf = useSessionTitle();
  const where = session ? titleOf(session).title : null;
  const layout = ({ body, footer }: AskParts): ReactNode => (
    <>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-contain px-(--dialog-padding) pb-4">
        {/* No helper line under it: the time left is in the header, and the sheet says nothing else. */}
        {body}
      </div>
      {footer && <div className="shrink-0 border-t border-border-subtle px-(--dialog-padding) py-3">{footer}</div>}
    </>
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      size="md"
      title={
        <span className="flex min-w-0 items-center gap-2">
          <Hand className="size-4 shrink-0 text-status-attention" aria-hidden />
          <span className="min-w-0 truncate text-status-attention-ink">{askHeading(a)}</span>
        </span>
      }
      description={
        <span className="flex min-w-0 items-center gap-2 text-caption">
          {where && <span className="min-w-0 truncate">{where}</span>}
          {a.state === 'pending' && <TimeLeft a={a} now={now} />}
        </span>
      }
      // The body scrolls inside; the send bar below it stays put.
      bodyClassName="flex flex-col overflow-hidden p-0"
    >
      <AskAnswer approval={a} surface="drawer" render={layout} />
    </Dialog>
  );
}

export function AnswerSheet() {
  const id = useAnswerSheet((s) => s.id);
  const close = useAnswerSheet((s) => s.close);
  const navigate = useNavigate();
  const [, openSession] = useSessionParam();
  const { approvals } = useApprovals();
  const blocked = useBlockedApprovals().items;
  const machines = useOrgus((s) => s.machines);
  const me = useMeId();
  const name = usePersonName();
  const pending = id ? approvals?.pending.find((a) => a.id === id && a.state === 'pending') : undefined;
  const block = id && !pending ? blocked.find((b) => b.approval.id === id) : undefined;
  const title = pending?.title ?? block?.block.label ?? 'Request';
  const inbox = () => {
    if (id) navigate(`/inbox?approval=${encodeURIComponent(id)}`);
    close();
  };

  if (pending && isAnswerable(pending)) return <AskSheet a={pending} onClose={close} />;

  let body;
  if (pending) body = <RequestCard a={pending} variant="bare" onDone={close} />;
  else if (block) {
    const v = blockView(block.block, { machineName: machines[block.approval.machineId]?.name ?? 'its machine', grantUntil: grantUntil(approvals, block.approval), sessionStatus: block.session?.status, me, name });
    const sessionId = block.approval.sessionId;
    body = (
      <>
        <ApprovalBlockCard block={block.block} v={v} approvalId={block.approval.id} decidedAt={block.approval.decidedAt ?? block.approval.createdAt} />
        {/* The whole session is one step away, never the first thing a blocked item opens. */}
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="sm"
            icon={<SquareTerminal />}
            onClick={() => {
              close();
              openSession(sessionId);
            }}
          >
            Open session
          </Button>
        </div>
      </>
    );
  } else
    body = (
      <EmptyState
        icon={<CircleCheckBig />}
        title="Nothing to answer"
        description="Someone answered it, or the session moved on."
        action={
          <Button variant="secondary" icon={<Inbox />} onClick={inbox}>
            Open the Inbox
          </Button>
        }
      />
    );

  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && close()} title={title} hideTitle size="md">
      <div className="flex flex-col gap-3">{body}</div>
    </Dialog>
  );
}
